import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Heart, MessageSquare, Send, ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { getPost, addReply, likePost } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Textarea } from '../components/ui/textarea';
import RichText from '../components/RichText';

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function Avatar({ src, name, size = 32 }) {
  const cls = 'rounded-full flex items-center justify-center font-bold shrink-0';
  if (src) return <img src={src} alt={name} referrerPolicy="no-referrer" className={`${cls} object-cover`} style={{ width: size, height: size }} />;
  return <span className={`${cls} bg-[#2E6BFF]/12 text-[#2E6BFF]`} style={{ width: size, height: size, fontSize: size * 0.4 }}>{name?.charAt(0).toUpperCase()}</span>;
}

export default function Thread() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const [post, setPost] = useState(null);
  const [reply, setReply] = useState({ content: '', image: null });
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const load = () => getPost(id).then(setPost).catch(() => setPost(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const liked = post && user && (post.liked_by || []).includes(user.user_id);

  const like = async () => {
    if (!user) { toast.error('Sign in to like posts.'); login(); return; }
    try {
      const res = await likePost(id);
      setPost((p) => ({
        ...p,
        likes: res.likes,
        liked_by: res.liked ? [...(p.liked_by || []), user.user_id] : (p.liked_by || []).filter((x) => x !== user.user_id),
      }));
    } catch (e) { toast.error('Could not like.'); }
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB.'); return; }
    const b64 = await fileToBase64(file);
    setReply((r) => ({ ...r, image: b64 }));
  };

  const submit = async () => {
    if (!user) { toast.error('Sign in to reply.'); login(); return; }
    if (!reply.content) { toast.error('Write a reply first.'); return; }
    setSending(true);
    try {
      await addReply(id, reply);
      setReply({ content: '', image: null });
      toast.success('Reply added!');
      load();
    } catch (e) {
      if (e?.response?.status === 401) { toast.error('Please sign in.'); login(); }
      else toast.error('Could not add reply.');
    } finally { setSending(false); }
  };

  if (post === false) return <div className="max-w-3xl mx-auto px-5 pt-20 text-center text-[#5B6472]">Discussion not found. <Link to="/community" className="text-[#2E6BFF]">Back to community</Link></div>;
  if (!post) return <div className="max-w-3xl mx-auto px-5 pt-20 text-center text-[#5B6472]">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-10">
      <Link to="/community" className="inline-flex items-center gap-1.5 text-[#5B6472] hover:text-[#2E6BFF] text-sm font-medium"><ArrowLeft size={16} /> Back to community</Link>

      <article className="bg-white rounded-[24px] p-7 sm:p-9 mt-5 shadow-[0_25px_70px_-48px_rgba(14,23,38,0.4)]">
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${post.category_color || '#2E6BFF'}1a`, color: post.category_color || '#2E6BFF' }}>{post.category_name || post.category}</span>
        <h1 className="font-display font-extrabold text-[#0E1726] text-[28px] sm:text-[34px] tracking-tight leading-tight mt-4">{post.title}</h1>
        <div className="flex items-center gap-3 mt-3 text-sm text-[#5B6472]">
          <Avatar src={post.author_picture} name={post.author} />
          <span className="font-medium text-[#0E1726]">{post.author}</span>
          <span>· {timeAgo(post.created_at)}</span>
        </div>
        <div className="text-[#334155] text-[17px] leading-relaxed mt-5"><RichText text={post.content} /></div>
        {post.image && <img src={post.image} alt="attachment" className="mt-5 rounded-xl border border-black/10 max-h-96" />}
        <div className="flex items-center gap-4 mt-6 pt-5 border-t border-black/5">
          <button onClick={like} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${liked ? 'bg-[#2E6BFF] text-white' : 'bg-[#F4F4F7] text-[#0E1726] hover:bg-[#e9e9ef]'}`}>
            <Heart size={16} fill={liked ? 'currentColor' : 'none'} /> {post.likes}
          </button>
          <span className="flex items-center gap-2 text-sm text-[#5B6472]"><MessageSquare size={16} /> {post.replies.length} replies</span>
        </div>
      </article>

      <section className="mt-8">
        <h2 className="font-display font-bold text-[#0E1726] text-[20px] mb-4">{post.replies.length} Replies</h2>
        <div className="space-y-3">
          {post.replies.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl p-5 shadow-[0_20px_50px_-46px_rgba(14,23,38,0.4)]">
              <div className="flex items-center gap-3 text-sm">
                <Avatar src={r.author_picture} name={r.author} size={28} />
                <span className="font-semibold text-[#0E1726]">{r.author}</span>
                <span className="text-[#5B6472]">· {timeAgo(r.created_at)}</span>
              </div>
              <div className="text-[#334155] leading-relaxed mt-2.5"><RichText text={r.content} /></div>
              {r.image && <img src={r.image} alt="attachment" className="mt-3 rounded-lg border border-black/10 max-h-72" />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-6 mt-5 shadow-[0_20px_50px_-46px_rgba(14,23,38,0.4)]">
          <h3 className="font-display font-bold text-[#0E1726] text-[17px] mb-3">Join the conversation</h3>
          {user ? (
            <>
              <div className="flex items-center gap-2.5 mb-3">
                <Avatar src={user.picture} name={user.name} size={28} />
                <span className="text-sm font-medium text-[#0E1726]">{user.name}</span>
              </div>
              <Textarea className="min-h-[100px]" placeholder="Share your thoughts... Use **bold** and @mention people." value={reply.content} onChange={(e) => setReply({ ...reply, content: e.target.value })} />
              {reply.image ? (
                <div className="relative inline-block mt-3">
                  <img src={reply.image} alt="preview" className="max-h-32 rounded-lg border border-black/10" />
                  <button onClick={() => setReply({ ...reply, image: null })} className="absolute -top-2 -right-2 bg-[#0E1B2E] text-white rounded-full w-6 h-6 flex items-center justify-center"><X size={14} /></button>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 text-sm font-semibold text-[#2E6BFF] mt-3"><ImagePlus size={16} /> Add an image</button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
              <div>
                <button onClick={submit} disabled={sending} className="mt-4 bg-[#0E1B2E] text-white font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:bg-[#1b2a44] transition-colors disabled:opacity-60">
                  {sending ? 'Posting...' : 'Post reply'} <Send size={16} />
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-[#5B6472] mb-4">Sign in to join the conversation.</p>
              <button onClick={login} className="bg-[#2E6BFF] text-white font-semibold px-6 py-3 rounded-full">Continue with Google</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
